/**
 * Who the app thinks is signed in, derived from what Supabase last reported.
 *
 * `(token, email, userId) → AuthState` is a pure function, and it was living inside a
 * `useEffect` callback in AuthContext.tsx where no test could reach it. What is left behind
 * after this extraction is `useEffect(() => onAuthChange(cb), [])` — subscribe on mount,
 * unsubscribe on unmount — which is an assertion that React runs effects, not that this app is
 * correct. So this module is the whole of the decision, and the provider is now wiring.
 *
 * It deliberately knows nothing about: React, Supabase, the network, the 10-second timeout that
 * may deliver `null` instead of a session, and how the API client is built — that arrives as an
 * argument so this function can be called without one.
 *
 * The direction that matters is the same one session-token.ts is written around: answering
 * "unauthenticated" too often only sends someone to a login form, which is recoverable and
 * obvious. Answering "authenticated" on a value that is not a credential propagates — the
 * authenticated shell opens, /login redirects the visitor away from the only way back, the token
 * is forwarded to the extension where it outlives the tab, and every request goes out as
 * `Authorization: Bearer <placeholder>`. So the token is put through the same `usableToken` rule
 * the route guard uses rather than a truthiness check, and a session that reports itself signed
 * in with a blank or whitespace token is not signed in.
 */
import { usableToken } from "./session-token";
import type { ApiClient } from "./api";

export type AuthState =
  | { status: "loading" }
  | { status: "unauthenticated" }
  | {
      status: "authenticated";
      token: string;
      email: string | null;
      /**
       * Opake Supabase-User-ID. Existiert, damit Telemetrie einen Identifier hat, der
       * KEINE E-Mail ist — PostHog wurde zuvor mit der Adresse als distinct_id
       * identifiziert (siehe routes/_authenticated.tsx).
       */
      userId: string | null;
      api: ApiClient;
    };

/** What `onAuthChange` can produce: it never reports "loading", only a settled answer. */
export type SettledAuthState = Extract<AuthState, { status: "authenticated" } | { status: "unauthenticated" }>;

/**
 * The state the app must be in, given what Supabase last said.
 *
 * `createApi` is injected rather than imported so the derivation stays callable on its own, and
 * because it pins something worth pinning: the client is built from the SAME token that is
 * stored on the state. Built from a stale one, every request would be authorised as the previous
 * session while the UI shows the current user.
 */
export function deriveAuthState(
  token: string | null | undefined,
  email: string | null | undefined,
  userId: string | null | undefined,
  createApi: (token: string) => ApiClient,
): SettledAuthState {
  const usable = usableToken(token);
  if (!usable) return { status: "unauthenticated" };
  return {
    status: "authenticated",
    token: usable,
    email: email ?? null,
    userId: userId ?? null,
    api: createApi(usable),
  };
}

/** What the error tracker should be told about the person now using the app. */
export type SentryIdentity =
  | { kind: "set"; id: string }
  | { kind: "clear" }
  | { kind: "leave" };

/**
 * Only ever the opaque user id — never the account email.
 *
 * lib/sentry.ts states the rule ("keine personenbezogenen Inhaltsdaten", and Sentry is in no
 * processor list) and enforces it in its own signature; this is the second place the decision is
 * made, so it is pinned here too rather than trusted twice.
 *
 * `leave` preserves the existing behaviour for the authenticated-but-no-userId case rather than
 * quietly changing it. It is unreachable in practice — a Supabase session always carries
 * `user.id`, so a token without one does not occur — but if it ever did, the previous user's id
 * would stay attached and their name would be on someone else's errors. Recorded rather than
 * fixed because changing it is a behaviour change with no observed consequence to justify it.
 */
export function sentryIdentityFor(state: SettledAuthState): SentryIdentity {
  if (state.status !== "authenticated") return { kind: "clear" };
  if (!state.userId) return { kind: "leave" };
  return { kind: "set", id: state.userId };
}
